import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TooltipProvider } from './components/ui/tooltip';
import { Toaster } from './components/ui/toaster';
import { ModeProvider } from './contexts/ModeContext';
import { AppShell } from './components/layout/AppShell';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const PositionsPage = React.lazy(() => import('./pages/PositionsPage'));
const PositionDetailPage = React.lazy(() => import('./pages/PositionDetailPage'));
const TradesPage = React.lazy(() => import('./pages/TradesPage'));
const TradeDetailPage = React.lazy(() => import('./pages/TradeDetailPage'));
const SignalsPage = React.lazy(() => import('./pages/SignalsPage'));
const SignalDetailPage = React.lazy(() => import('./pages/SignalDetailPage'));
const RiskMonitorPage = React.lazy(() => import('./pages/RiskMonitorPage'));
const AlertsPage = React.lazy(() => import('./pages/AlertsPage'));
const AuditPage = React.lazy(() => import('./pages/AuditPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const SettingsHistoryPage = React.lazy(() => import('./pages/SettingsHistoryPage'));
const HowItWorksPage = React.lazy(() => import('./pages/HowItWorksPage'));
const Mt5StatusPage = React.lazy(() => import('./pages/Mt5StatusPage'));
const AdminUsers = React.lazy(() => import('./pages/AdminUsers'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30000,
    },
  },
});

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="text-muted-foreground">Loading...</div>
    </div>
  );
}

function AuthOrLogin() {
  const [isAuthed, setIsAuthed] = React.useState<boolean | null>(null);
  React.useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/auth/me');
        setIsAuthed(res.ok);
      } catch {
        setIsAuthed(false);
      }
    })();
  }, []);
  if (isAuthed === null) return <div className="p-4 text-center text-sm text-muted-foreground">Loading...</div>;
  return isAuthed ? <Navigate to="/dashboard" replace /> : <Login />;
}

function ProtectedDashboardLayout() {
  const [isAuthed, setIsAuthed] = React.useState<boolean | null>(null);
  
  React.useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/auth/me');
        setIsAuthed(res.ok);
      } catch {
        setIsAuthed(false);
      }
    })();
  }, []);

  if (isAuthed === null) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthed) {
    return <Navigate to="/login" replace />;
  }

  return (
    <ModeProvider>
      <AppShell />
    </ModeProvider>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <React.Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/login" element={<AuthOrLogin />} />
            <Route path="/forgot" element={<ForgotPassword />} />
            <Route path="/reset" element={<ResetPassword />} />
            
            <Route element={<ProtectedDashboardLayout />}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/home" element={<Navigate to="/dashboard" replace />} />
              <Route path="/positions" element={<PositionsPage />} />
              <Route path="/positions/:id" element={<PositionDetailPage />} />
              <Route path="/trades" element={<TradesPage />} />
              <Route path="/trades/:id" element={<TradeDetailPage />} />
              <Route path="/signals" element={<SignalsPage />} />
              <Route path="/signals/:id" element={<SignalDetailPage />} />
              <Route path="/risk" element={<RiskMonitorPage />} />
              <Route path="/mt5-status" element={<Mt5StatusPage />} />
              <Route path="/alerts" element={<AlertsPage />} />
              <Route path="/audit" element={<AuditPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/settings/history" element={<SettingsHistoryPage />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute roles={['admin']}>
                    <AdminUsers />
                  </ProtectedRoute>
                }
              />
            </Route>
          </Routes>
        </React.Suspense>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}
